import { Button, Headline, Input, Text } from 'forly-kit';
import { colors } from 'forly-kit/dist/colors';
import React, { useState } from 'react';
import styles from './changePassForm.module.scss';
import { SubmitHandler, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { passwordPattern } from 'src/helpers/patterns';
import { IActionForm } from './../form.types';
import { IChangePassFormData } from './changePassForm.types';

const Form: React.FC<IActionForm<IChangePassFormData>> = ({
	action,
	isPending,
}) => {
	const {t} = useTranslation();

	const {
		register,
		formState: {errors},
		setError,
		getValues,
		handleSubmit,
		control
	} = useForm<IChangePassFormData>({
		mode: 'onChange'
	});

	const { dirtyFields, isValidating, touchedFields} = useFormState({control});
	const [passError, setPassError] = useState<string>('');

	const onSubmit: SubmitHandler<IChangePassFormData> = async (data) => {
		const {password, confirmPassword} = data;
		if (password !== confirmPassword) {
			setError('confirmPassword', {
				type: 'pattern',
				message: t('errors.samePassword')
			});
			return;
		}
		
		action(data);
	};

	const getFieldStatus = (name: keyof IChangePassFormData) => {
		if (!dirtyFields[name] && !touchedFields[name]) {
			return null;
		}
		if (errors[name]) {
			return 'error';
		} else {
			return 'success';
		}
	};

	const changePasswordHandler = (el: any) => {
		setError('confirmPassword', {});
		setPassError('');
		const pass = getValues('password');
		if (pass !== el.currentTarget.value) {
			setPassError(t('errors.samePassword'));
		}
	};

	const passwordFieldStatus = React.useMemo<'error' | 'success' | null>(() => {
		return getFieldStatus('password');
	}, [isValidating]);

	const passwordStatus = React.useMemo<'error' | 'success' | null>(() => {
		if (passError) {
			return 'error';
		}
		return getFieldStatus('confirmPassword');
	}, [isValidating, errors.confirmPassword]);

	return <>
		<Headline type="h0" color={colors.primary800} className={styles.title}>
			{t('changePassTitle')}
		</Headline>
		<div className={styles.subtitle}>
			<Text size='m' color={colors.primary900}>
				{t('changePassSubtitle1')}
			</Text>
			<Text size='m' color={colors.primary900}>
				{t('changePassSubtitle2')}
			</Text>
		</div>

		<form
			className={styles.form}
			onSubmit={handleSubmit(onSubmit)}
		>
			<Input
				type='password'
				label={t('newPass')}
				register={() => register('password', {
					required: {
						value: true,
						message: t('errors.requiredField')
					},
					pattern: {
						value: passwordPattern,
						message: t('errors.invalidPasswordPattern')
					}
				})}
				status={passwordFieldStatus}
				errorMsg={errors.password?.message}
			/>
			<Input
				label={t('confirmPass')}
				type='password'
				register={() => register('confirmPassword', {
					required: {
						value: true,
						message: t('errors.requiredField')
					},
					onChange: changePasswordHandler
				})}
				status={passwordStatus === 'success' ? null : passwordStatus}
				errorMsg={errors.confirmPassword?.message || passError}
			/>
			<Button
				className={styles.btn}
				text={t('save')}
				variant='primary'
				type='submit'
				fill
				disabled={!!errors.password || !!errors.confirmPassword || isPending}
			/>
		</form>
	</>;
};

export const ChangePassForm = React.memo(Form);